/**
 * Fiore hair quiz
 *
 * Renders as a single custom element. All copy and product data are rendered
 * server-side by sections/fiore-hair-quiz.liquid; this file owns state,
 * navigation, the recommendation rules and add-to-cart.
 *
 * Recommendation rules (from the brief):
 *   Q1 picks the base shampoo — oily/congested scalp gets the clarifying wash,
 *   everything else gets the moisturising wash.
 *   Q2 + Q3 pick the companion(s):
 *     fine/thin                          -> base + leave-in
 *     medium|thick + heat and/or colour  -> base + conditioner + leave-in
 *     medium|thick + none/water/humidity -> base + conditioner
 *   Q4 does not affect the routine.
 *   Q5 selects the result headline and subtitle.
 */

const TOTAL_STEPS = 5;
const ADVANCE_DELAY = 320;
const ANALYSING_DELAY = 1400;

/** Product keys, in the order they should appear in a routine. */
const ROUTINE_ORDER = ['clarifying_shampoo', 'moisturising_shampoo', 'conditioner', 'leave_in'];

class FioreQuiz extends HTMLElement {
  connectedCallback() {
    this.answers = { q1: null, q2: null, q3: [], q4: null, q5: null };
    this.removed = new Set(); // product keys the customer dropped from the routine
    this.lastRemoved = null;
    this.current = 0; // 0 = intro, 1..5 = questions
    this.products = this.#readProducts();
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.cartUrl = this.dataset.cartUrl || '/cart';
    this.bundlePercent = Number(this.dataset.bundlePercent) || 0;
    this.bundleCode = (this.dataset.bundleCode || '').trim();
    this.bundleMinItems = Number(this.dataset.bundleMinItems) || 2;

    this.progress = this.querySelector('[data-progress]');
    this.fill = this.querySelector('[data-progress-fill]');
    this.count = this.querySelector('[data-progress-count]');
    this.nudge = this.querySelector('[data-progress-nudge]');
    this.backBtn = this.querySelector('[data-action="back"]');
    this.actions = this.querySelector('[data-actions]');
    this.nextBtn = this.querySelector('[data-action="next"]');
    this.stickyBuy = this.querySelector('[data-sticky-buy]');

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);

    this.#show(0);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
  }

  /**
   * The bar is permanent for the whole result step, at every width: it carries
   * the discount codes as well as checkout, so gating it on scroll would hide
   * the offers from anyone whose screen fits the summary without scrolling.
   */
  #refreshStickyBuy() {
    if (!this.stickyBuy) return;

    const onResult = Boolean(this.querySelector('[data-view="result"][data-active]'));
    const blocked = this.stickyBuy.dataset.blocked === 'true';
    const show = onResult && !blocked;

    this.stickyBuy.hidden = !show;
    // Lets the result view reserve room for the bar rather than sitting under it.
    this.classList.toggle('fq--has-sticky', show);
  }

  #readProducts() {
    const node = this.querySelector('[data-quiz-products]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch {
      return {};
    }
  }

  /* ---------------------------------------------------------------- events */

  #onClick = (event) => {
    const trigger = event.target.closest('[data-action]');
    if (!trigger || !this.contains(trigger)) return;

    switch (trigger.dataset.action) {
      case 'start':
        this.#show(1);
        break;
      case 'next':
        this.#advance();
        break;
      case 'back':
        if (this.current > 0) this.#show(this.current - 1);
        break;
      case 'retake':
        this.#reset();
        break;
      case 'add-all':
        this.#addAll(trigger);
        break;
      case 'buy-now':
        this.#buyNow(trigger);
        break;
      case 'remove-product':
        this.#removeProduct(trigger.dataset.removeKey);
        break;
      case 'undo-remove':
        this.#undoRemove();
        break;
    }
  };

  #onChange = (event) => {
    const input = event.target;
    if (!input.name || !input.name.startsWith('q')) return;

    if (input.type === 'checkbox') {
      this.#syncExclusive(input);
      this.answers[input.name] = this.#checkedValues(input.name);
      this.#refreshNext();
      return;
    }

    this.answers[input.name] = input.dataset.value;

    // Single-choice questions advance on their own, after a beat so the
    // selection is visibly registered.
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.#advance(), ADVANCE_DELAY);
  };

  /**
   * "None of these" and the specific stressors are mutually exclusive.
   */
  #syncExclusive(changed) {
    const group = this.#inputs(changed.name);
    if (changed.checked && changed.dataset.exclusive === 'true') {
      group.forEach((input) => {
        if (input !== changed) input.checked = false;
      });
    } else if (changed.checked) {
      group.forEach((input) => {
        if (input.dataset.exclusive === 'true') input.checked = false;
      });
    }
  }

  #inputs(name) {
    return Array.from(this.querySelectorAll(`input[name="${name}"]`));
  }

  #checkedValues(name) {
    return this.#inputs(name)
      .filter((input) => input.checked)
      .map((input) => input.dataset.value);
  }

  /* -------------------------------------------------------------- movement */

  #advance() {
    if (!this.#isAnswered(this.current)) return;
    if (this.current >= TOTAL_STEPS) {
      this.#finish();
      return;
    }
    this.#show(this.current + 1);
  }

  #isAnswered(step) {
    if (step === 0) return true;
    const value = this.answers[`q${step}`];
    return Array.isArray(value) ? value.length > 0 : Boolean(value);
  }

  #show(step) {
    clearTimeout(this.timer);
    this.current = step;

    this.#activate(step === 0 ? '[data-view="intro"]' : `[data-view="step"][data-step="${step}"]`);

    const isQuestion = step > 0;
    this.progress.hidden = !isQuestion;
    this.backBtn.disabled = step <= 1;

    if (isQuestion) {
      this.fill.style.inlineSize = `${(step / TOTAL_STEPS) * 100}%`;
      this.count.textContent = `${step} / ${TOTAL_STEPS}`;
      this.#setNudge(step);
    }

    this.#refreshNext();
    this.#refreshStickyBuy();
    this.#focusView();
    this.#scrollUp();
  }

  /**
   * Reassurance on the closing steps — telling people how little is left
   * measurably reduces late abandonment.
   */
  #setNudge(step) {
    if (!this.nudge) return;
    // Questions still to come after this one.
    const remaining = TOTAL_STEPS - step;
    let text = '';
    if (remaining === 0) text = 'Last question';
    else if (remaining === 1) text = 'Almost done';

    this.nudge.textContent = text;
    this.nudge.hidden = text === '';
  }

  /**
   * Continue is only rendered for multi-select questions; single-choice
   * questions advance themselves.
   */
  #refreshNext() {
    const view = this.querySelector('[data-view][data-active]');
    const isMulti = view?.dataset.multi === 'true';
    this.actions.hidden = !isMulti;
    // Only the steps showing the bar reserve room for it.
    this.classList.toggle('fq--has-actions', isMulti);
    if (isMulti) this.nextBtn.disabled = !this.#isAnswered(this.current);
  }

  #activate(selector) {
    this.querySelectorAll('[data-view]').forEach((view) => {
      delete view.dataset.active;
    });
    const next = this.querySelector(selector);
    if (next) next.dataset.active = 'true';
  }

  #focusView() {
    const heading = this.querySelector('[data-view][data-active] [data-focus]');
    if (heading) heading.focus({ preventScroll: true });
  }

  #scrollUp() {
    const top = this.getBoundingClientRect().top + window.scrollY - 16;
    if (window.scrollY > top) {
      window.scrollTo({ top, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  }

  #reset() {
    this.answers = { q1: null, q2: null, q3: [], q4: null, q5: null };
    this.removed.clear();
    this.lastRemoved = null;
    this.#showRemovedNotice(null);
    this.querySelectorAll('.fq__input').forEach((input) => {
      input.checked = false;
    });
    this.#show(1);
  }

  /* -------------------------------------------------------- recommendation */

  /** @returns {string[]} product keys in routine order */
  #routine() {
    const { q1, q2, q3 } = this.answers;
    const keys = [q1 === 'oily' ? 'clarifying_shampoo' : 'moisturising_shampoo'];

    if (q2 === 'fine') {
      keys.push('leave_in');
    } else {
      keys.push('conditioner');
      if (q3.includes('heat') || q3.includes('chemical')) keys.push('leave_in');
    }

    // Only recommend what the store can actually sell.
    return ROUTINE_ORDER.filter((key) => keys.includes(key) && this.products[key]);
  }

  #finish() {
    this.#activate('[data-view="loading"]');
    this.progress.hidden = true;
    this.actions.hidden = true;
    this.#scrollUp();

    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.#renderResult(), ANALYSING_DELAY);
  }

  #renderResult() {
    const goal = this.querySelector(`[data-goal="${this.answers.q5}"]`);
    const title = this.querySelector('[data-result-title]');
    const text = this.querySelector('[data-result-text]');
    if (goal) {
      title.textContent = goal.dataset.headline;
      text.textContent = goal.dataset.subtitle;
    }

    this.#paintRoutine();

    this.#activate('[data-view="result"]');
    // After #activate, not before: #paintRoutine's own refresh runs while the
    // result view is still inactive, so the bar would evaluate to hidden and
    // nothing would re-run. This path bypasses #show(), which is where every
    // other view gets its refresh.
    this.#refreshStickyBuy();
    this.#focusView();
    this.#scrollUp();
  }

  /**
   * Paints the cards, totals and CTAs for the current routine. Split out from
   * #renderResult so removing a product can repaint without replaying the
   * analysing state.
   */
  #paintRoutine() {
    const keys = this.#routine().filter((key) => !this.removed.has(key));
    this.routineKeys = keys;

    let total = 0;
    let compareTotal = 0;

    this.querySelectorAll('[data-product-key]').forEach((card) => {
      card.hidden = !keys.includes(card.dataset.productKey);
    });

    // Walk the routine, not the DOM: appending in order and numbering in the
    // same pass keeps the step labels correct after a remove or undo, which
    // leave the DOM in a different order than the routine.
    const list = this.querySelector('[data-cards]');
    keys.forEach((key, index) => {
      const card = list.querySelector(`[data-product-key="${key}"]`);
      if (!card) return;

      list.append(card);
      const label = card.querySelector('[data-step-label]');
      if (label) label.textContent = `Step ${index + 1} · ${card.dataset.stepName}`;

      const price = Number(this.products[key].price) || 0;
      total += price;
      // Fall back to the price so unmarked-down items don't inflate the saving.
      compareTotal += Number(this.products[key].compare_at) || price;
    });

    // Two independent sources of saving: per-product compare-at prices, and a
    // bundle discount for building the full routine. Both fold into one figure.
    this.bundleActive = this.bundlePercent > 0 && keys.length >= this.bundleMinItems;
    const bundleCut = this.bundleActive ? Math.round((total * this.bundlePercent) / 100) : 0;
    const payable = total - bundleCut;
    const wasTotal = Math.max(compareTotal, total);
    const saving = wasTotal - payable;

    const totalEl = this.querySelector('[data-total]');
    if (totalEl) totalEl.textContent = this.#money(payable);

    const compareEl = this.querySelector('[data-total-compare]');
    const saveEl = this.querySelector('[data-total-save]');
    const saveText = this.querySelector('[data-total-save-text]');

    if (compareEl) {
      compareEl.hidden = saving <= 0;
      if (saving > 0) compareEl.textContent = this.#money(wasTotal);
    }

    if (saveEl) {
      saveEl.hidden = saving <= 0;
      if (saving > 0 && saveText) {
        const percent = Math.round((saving / wasTotal) * 100);
        const suffix = this.bundleActive ? ' with your routine bundle' : ' on this routine';
        saveText.textContent = `You save ${this.#money(saving)} (${percent}%)${suffix}`;
      }
    }

    const empty = keys.length === 0;
    const unavailable = !empty && keys.some((key) => !this.products[key].available);
    const blocked = empty || unavailable;

    const addBtn = this.querySelector('[data-action="add-all"]');
    if (addBtn) {
      addBtn.disabled = blocked;
      if (empty) addBtn.textContent = 'Your routine is empty';
      else if (unavailable) addBtn.textContent = 'Currently unavailable';
      else addBtn.textContent = `Add ${keys.length} ${keys.length === 1 ? 'product' : 'products'} to cart`;
    }

    // querySelectorAll, not querySelector: the sticky bar carries a second
    // Buy it now that has to track the same state as the inline one.
    this.querySelectorAll('[data-action="buy-now"]').forEach((buyBtn) => {
      buyBtn.disabled = blocked;
      buyBtn.hidden = blocked;
    });

    if (this.stickyBuy) {
      this.stickyBuy.dataset.blocked = blocked ? 'true' : 'false';

      const stickyTotal = this.querySelector('[data-sticky-total]');
      if (stickyTotal) stickyTotal.textContent = this.#money(payable);

      const stickyCompare = this.querySelector('[data-sticky-compare]');
      if (stickyCompare) {
        stickyCompare.hidden = saving <= 0;
        if (saving > 0) stickyCompare.textContent = this.#money(wasTotal);
      }
    }

    this.#refreshStickyBuy();
  }

  /* --------------------------------------------------------- editing */

  /**
   * Removing a step is better than abandoning: someone who only wants the
   * shampoo can drop the rest instead of leaving. Always undoable.
   */
  #removeProduct(key) {
    if (!key || this.removed.has(key)) return;

    this.removed.add(key);
    this.lastRemoved = key;
    this.#paintRoutine();
    this.#showRemovedNotice(key);
  }

  #undoRemove() {
    if (!this.lastRemoved) return;

    this.removed.delete(this.lastRemoved);
    this.lastRemoved = null;
    this.#paintRoutine();
    this.#showRemovedNotice(null);
  }

  #showRemovedNotice(key) {
    const notice = this.querySelector('[data-removed]');
    const text = this.querySelector('[data-removed-text]');
    if (!notice) return;

    notice.hidden = !key;
    if (key && text) {
      const card = this.querySelector(`[data-product-key="${key}"]`);
      const title = card?.querySelector('.fq-card__title')?.textContent.trim() || 'Product';
      text.textContent = `${title} removed.`;
    }
  }

  /* ------------------------------------------------------------------ cart */

  /** @returns {{id: number, quantity: number}[]} line items for the routine */
  #lineItems() {
    return (this.routineKeys || [])
      .map((key) => this.products[key])
      .filter((product) => product && product.variant_id)
      .map((product) => ({ id: product.variant_id, quantity: 1 }));
  }

  /**
   * POSTs the routine to the cart. Both CTAs share this; only what happens
   * afterwards differs.
   *
   * @returns {Promise<boolean>} whether the items were added
   */
  async #postToCart(button, pendingLabel) {
    const items = this.#lineItems();
    if (!items.length) return false;

    const error = this.querySelector('[data-cart-error]');
    if (error) error.hidden = true;

    this.#setBusy(true);
    const original = button.textContent;
    button.textContent = pendingLabel;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) throw new Error(`Cart responded ${response.status}`);
      return true;
    } catch {
      this.#setBusy(false);
      button.textContent = original;
      if (error) error.hidden = false;
      return false;
    }
  }

  /** Disable both CTAs together so the routine can't be added twice. */
  #setBusy(busy) {
    this.querySelectorAll('[data-action="add-all"], [data-action="buy-now"]').forEach((button) => {
      button.disabled = busy;
    });
  }

  /**
   * Registers the bundle discount code against the session so it is already
   * applied when the customer reaches checkout, whichever CTA they used.
   * A no-op when the merchant uses an automatic discount instead.
   */
  async #applyBundleCode() {
    if (!this.bundleCode || !this.bundleActive) return;
    const root = window.Shopify?.routes?.root || '/';
    try {
      await fetch(`${root}discount/${encodeURIComponent(this.bundleCode)}`, { method: 'GET' });
    } catch {
      // Non-fatal: the customer can still enter the code at checkout.
    }
  }

  async #addAll(button) {
    const added = await this.#postToCart(button, 'Adding…');
    if (!added) return;

    await this.#applyBundleCode();

    button.textContent = 'Added to cart';
    // Leave Buy it now clickable so the customer can still check out.
    this.querySelectorAll('[data-action="buy-now"]').forEach((buyNow) => {
      buyNow.disabled = false;
    });

    await this.#notifyCart();
  }

  /**
   * Add the routine, then hand the cart to GoKwik's checkout. GoKwik's own
   * buyNow flow only understands a single variant read from a product form,
   * so a multi-product routine has to go through the cart.
   */
  async #buyNow(button) {
    const added = await this.#postToCart(button, 'Redirecting…');
    if (!added) return;

    await this.#applyBundleCode();

    const sdk = await this.#gokwikSdk();
    if (sdk && window.merchantInfo) {
      sdk.initCheckout(window.merchantInfo);
      this.#setBusy(false);
      button.textContent = 'Buy it now';
      return;
    }

    // No GoKwik on the page (or it failed to load) — use native checkout.
    window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
  }

  /**
   * Resolve GoKwik's SDK, waiting briefly for its `gokwikLoaded` event since
   * the script loads async.
   *
   * @returns {Promise<object|null>}
   */
  #gokwikSdk() {
    const existing = window.gokwikSdk || window.__gkSdkCache;
    if (existing) return Promise.resolve(existing);

    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timeout);
        window.removeEventListener('gokwikLoaded', done);
        resolve(window.gokwikSdk || window.__gkSdkCache || null);
      };
      const timeout = setTimeout(done, 3000);
      window.addEventListener('gokwikLoaded', done);
    });
  }

  /**
   * Tell the theme its cart changed so the drawer/bubble update. If the theme
   * modules are unavailable for any reason, fall back to the cart page.
   */
  async #notifyCart() {
    try {
      const [{ CartAddEvent }, cart] = await Promise.all([
        import(this.dataset.eventsUrl),
        fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((response) => response.json()),
      ]);
      document.dispatchEvent(new CartAddEvent(cart, 'fiore-hair-quiz', { source: 'fiore-hair-quiz' }));
    } catch {
      window.location.href = this.cartUrl;
    }
  }

  /* ---------------------------------------------------------------- helpers */

  /** Minimal Shopify money formatter for the shop's configured format string. */
  #money(cents) {
    const format = this.moneyFormat;
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return format;

    const value = Number(cents) / 100;
    const [type] = match.slice(1);
    const decimals = type.includes('no_decimals') ? 0 : 2;

    let thousands = ',';
    let decimal = '.';
    if (type.includes('comma_separator')) {
      thousands = '.';
      decimal = ',';
    } else if (type.includes('apostrophe_separator')) {
      thousands = "'";
    }

    const fixed = value.toFixed(decimals);
    const [whole, fraction] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const amount = fraction ? `${grouped}${decimal}${fraction}` : grouped;

    return format.replace(match[0], amount);
  }
}

if (!customElements.get('fiore-quiz')) {
  customElements.define('fiore-quiz', FioreQuiz);
}
